import styles from './returnItemsTable.module.css'
import { useEffect, useMemo, useState } from "react"
import { Column, FieldType, FieldValue, NumberField, O, Section, Table } from "../../../../../features/ui"
import { Item, Operation } from "../../../../operations"
import { Lot, Product, ProductName, ProductType, useLotLoaders, useProductLoaders } from "../../../../products"
import { isDefined } from '../../../../../utils/typeUtils'
import { ReturnItemRow } from '../../types'

type Props = {
    operation: Operation
    defaultRows: ReturnItemRow[]
    title?: string
    onChange: (items: ReturnItemRow[]) => void
}

export const ReturnItemsTable = (props: Props) => {
    const { operation, defaultRows, title, onChange } = props

    const [products, setProducts] = useState<Product[]>([])
    const [lots, setLots] = useState<Lot[]>([])
    const [rows, setRows] = useState<ReturnItemRow[]>(defaultRows)

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    const load = async (items: Item[]) => {
        const { loadProducts } = productLoaders()
        const loadedProducts = await loadProducts(items.map(item => item.productId))
        setProducts(loadedProducts)
        const { loadProductsLots } = lotLoaders()
        const loadedLots = await loadProductsLots(loadedProducts.map(product => product.id))
        setLots(loadedLots)
    }

    useEffect(() => {
        load(operation.items)
    }, [operation.id])

    useEffect(() => {
        setRows(defaultRows)
    }, [defaultRows])

    useEffect(() => {
        onChange(rows)
    }, [rows])

    const onChangeReturnQuantity = (itemId: string) => (value?: FieldValue) => {
        const newReturnQuantity = value as O<number>
        if (!isDefined(newReturnQuantity)) return
        setRows(rows.map(row => {
            if (row.itemId !== itemId) return row
            const defectiveQuantity = Math.min(row.defectiveQuantity, newReturnQuantity!)
            return {
                ...row,
                returnQuantity: newReturnQuantity!,
                defectiveQuantity,
                nonDefectiveQuantity: newReturnQuantity! - defectiveQuantity
            }
        }))
    }

    const onChangeDefectiveQuantity = (itemId: string) => (value?: FieldValue) => {
        const newDefectiveQuantity = value as O<number>
        if (!isDefined(newDefectiveQuantity)) return
        setRows(rows.map(row => row.itemId === itemId ? {
            ...row,
            defectiveQuantity: newDefectiveQuantity!,
            nonDefectiveQuantity: row.returnQuantity - newDefectiveQuantity!
        } : row))
    }

    const onChangeNonDefectiveQuantity = (itemId: string) => (value?: FieldValue) => {
        const newNonDefectiveQuantity = value as O<number>
        if (!isDefined(newNonDefectiveQuantity)) return
        setRows(rows.map(row => row.itemId === itemId ? {
            ...row,
            nonDefectiveQuantity: newNonDefectiveQuantity!,
            defectiveQuantity: row.returnQuantity - newNonDefectiveQuantity!
        } : row))
    }

    const onClickQuantity = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation()
    }

    const columns: Column[] = useMemo(() => [
        {
            name: 'itemId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                const lot = lots.find(lot => lot.id === row.lotId)
                return <ProductName product={product} lot={lot} showSupplierCode />
            }
        },
        {
            name: 'delivered',
            type: FieldType.NUMBER,
            label: 'Entregados'
        },
        {
            name: 'returnQuantity',
            type: FieldType.NUMBER,
            label: 'A Devolver',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                return (
                    <NumberField
                        id="returnQuantity"
                        value={row.returnQuantity}
                        integer={product?.type !== ProductType.BULK}
                        min={0}
                        max={row.delivered}
                        className={styles.qtyField}
                        onChange={onChangeReturnQuantity(row.itemId)}
                        onClick={onClickQuantity}
                    />
                )
            }
        },
        {
            name: 'defectiveQuantity',
            type: FieldType.NUMBER,
            label: 'Con Defecto',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                return (
                    <NumberField
                        id="defectiveQuantity"
                        value={row.defectiveQuantity}
                        integer={product?.type !== ProductType.BULK}
                        min={0}
                        max={row.returnQuantity}
                        className={styles.qtyField}
                        onChange={onChangeDefectiveQuantity(row.itemId)}
                        onClick={onClickQuantity}
                    />
                )
            }
        },
        {
            name: 'nonDefectiveQuantity',
            type: FieldType.NUMBER,
            label: 'Sin Defecto',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                return (
                    <NumberField
                        id="nonDefectiveQuantity"
                        value={row.nonDefectiveQuantity}
                        integer={product?.type !== ProductType.BULK}
                        min={0}
                        max={row.returnQuantity}
                        className={styles.qtyField}
                        onChange={onChangeNonDefectiveQuantity(row.itemId)}
                        onClick={onClickQuantity}
                    />
                )
            }
        }
    ], [rows, products, lots])

    const tableRows = useMemo(() => rows.map(row => ({
        itemId: row.itemId,
        delivered: row.delivered,
        returnQuantity: row.itemId,
        defectiveQuantity: row.itemId,
        nonDefectiveQuantity: row.itemId
    })), [rows])

    return (
        <Section
            id="return-items"
            title={title || "Productos"}
            space={12}
        >
            <Table
                columns={columns}
                rows={tableRows}
                rowId="itemId"
                readOnly
                alignRight={false}
            />
        </Section>
    )
}
